import { useState, useMemo, useEffect } from "react";
import {
  Card, Row, Col, Table, Tag, DatePicker, Button,
  Space, Typography, Badge, Select, Progress,
  Divider, Tooltip as AntTooltip
} from "antd";
import {
  ShopOutlined, GlobalOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ClockCircleOutlined, HomeOutlined,
  RiseOutlined, CalendarOutlined, DollarOutlined, TeamOutlined,
  FilePdfOutlined
} from "@ant-design/icons";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, LabelList
} from "recharts";
import dayjs from "dayjs";
import { GeneralService } from "../Services/GeneralService";

import { Empty } from "antd";
import { canViewModule } from "../../utils/permissionUtils";
import logo from "../../assets/brand_logo1.png";

const MODULE_KEY = "dashboard";



const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const STATUS_CFG = {
  check_out: { color: "success", label: "Checked Out", hex: "#52c41a" },
  reserved: { color: "processing", label: "Reserved", hex: "#1677ff" },
  cancel: { color: "error", label: "Cancelled", hex: "#ff4d4f" },
  occupied: { color: "warning", label: "Occupied", hex: "#faad14" },
};
const ROOM_STATUS_CFG = {
  available: { color: "#52c41a", label: "Available" },
  occupied: { color: "#faad14", label: "Occupied" },
  reserved: { color: "#1677ff", label: "Reserved" },
};
const PIE_COLORS = ["#52c41a", "#1677ff", "#ff4d4f", "#faad14"];
const fmt = (n) => `Rs.${Number(n).toLocaleString("en-IN")}`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;



  return (
    <div style={{ background: "#fff", border: "1px solid #f0f0f0", borderRadius: 8, padding: "10px 14px", boxShadow: "0 4px 20px rgba(0,0,0,.08)" }}>
      <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, margin: 0, fontSize: 13 }}>
          {p.name}: {String(p.name).toLowerCase().includes("revenue") ? fmt(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

// ─── PDF Export ───────────────────────────────────────────────────────────────
const loadJsPDF = () =>
  new Promise((resolve) => {
    if (window.jspdf) return resolve(window.jspdf.jsPDF);
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    script.onload = () => resolve(window.jspdf.jsPDF);
    document.head.appendChild(script);
  });

const loadAutoTable = (jsPDF) =>
  new Promise((resolve) => {
    if (jsPDF.API?.autoTable) return resolve();
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
    script.onload = () => resolve();
    document.head.appendChild(script);
  });

const exportDashboardPDF = async ({
  dateRange,
  statusFilter,
  metrics,
  filteredBookRoom,
  room,
  revenueByRoom,
  roomWithLogCount,
  paymentModeData,
  bookLogModeData,
  statusPieData,
  timeline,
}) => {
  const JsPDF = await loadJsPDF();
  await loadAutoTable(JsPDF);

  const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentW = pageW - margin * 2;

  const fromDt = dateRange?.[0] ? dateRange[0].format("DD MMM YYYY, hh:mm A") : "All";
  const toDt = dateRange?.[1] ? dateRange[1].format("DD MMM YYYY, hh:mm A") : "All";
  const statusLabel = statusFilter === "all" ? "All Statuses" : STATUS_CFG[statusFilter]?.label || statusFilter;
  const generatedAt = dayjs().format("DD MMM YYYY, hh:mm A");

  // ── Colours ──
  const C = {
    primary: [30, 58, 138],      // deep blue
    accent: [99, 102, 241],      // indigo
    success: [82, 196, 26],
    warning: [250, 173, 20],
    danger: [255, 77, 79],
    info: [22, 119, 255],
    text: [30, 30, 40],
    muted: [120, 120, 140],
    bg: [248, 249, 252],
    white: [255, 255, 255],
    headerBg: [22, 32, 72],
  };

  const rgb = (arr) => `rgb(${arr.join(",")})`;

  let y = 0;

  // ── Helper: add page if needed ──
  const checkPage = (needed = 20) => {
    if (y + needed > pageH - 16) {
      doc.addPage();
      y = 16;
    }
  };

  // ── HEADER ──────────────────────────────────────────────────────────────────
  doc.setFillColor(...C.white);
  doc.rect(0, 0, pageW, 32, "F");

  const imgData = await new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      canvas.getContext("2d").drawImage(image, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    image.src = logo;
  });

  // Centered, wider logo
  const imgW = 60;
  const imgH = 20;
  const imgX = (pageW - imgW) / 2;
  doc.addImage(imgData, "PNG", imgX, 6, imgW, imgH);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(43, 42, 39);
  doc.text(`Generated: ${generatedAt}`, margin, 28);

  doc.setFontSize(8);
  doc.setTextColor(43, 42, 39);
  doc.text(`Confidential Report`, pageW - margin, 28, { align: "right" });

  y = 38;

  // ── FILTER BANNER ────────────────────────────────────────────────────────────
  doc.setFillColor(...C.bg);
  doc.roundedRect(margin, y, contentW, 20, 3, 3, "F");
  doc.setDrawColor(200, 210, 230);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, contentW, 20, 3, 3, "S");

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.primary);
  doc.text("APPLIED FILTERS", margin + 4, y + 7);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.text);
  const filterCol = contentW / 3;
  doc.setFontSize(7.5);
  doc.text(`Date From:`, margin + 4, y + 13);
  doc.setFont("helvetica", "bold");
  doc.text(fromDt, margin + 22, y + 13);

  doc.setFont("helvetica", "normal");
  doc.text(`Date To:`, margin + 4 + filterCol, y + 13);
  doc.setFont("helvetica", "bold");
  doc.text(toDt, margin + 4 + filterCol + 16, y + 13);

  doc.setFont("helvetica", "normal");
  doc.text(`Status:`, margin + 4 + filterCol * 2, y + 13);
  doc.setFont("helvetica", "bold");
  doc.text(statusLabel, margin + 4 + filterCol * 2 + 14, y + 13);

  y += 26;

  // ── SECTION TITLE HELPER ─────────────────────────────────────────────────────
  const sectionTitle = (title, icon = "") => {
    checkPage(16);
    doc.setFillColor(...C.accent);
    doc.rect(margin, y, 3, 9, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C.primary);
    doc.text(`${icon} ${title}`.trim(), margin + 7, y + 6.5);
    y += 13;
  };

  // ── STAT BOX HELPER ──────────────────────────────────────────────────────────
  const statBox = (x, bY, w, h, title, value, color, subtext = "") => {
    doc.setFillColor(color[0], color[1], color[2], 0.08);
    doc.setFillColor(...C.white);
    doc.roundedRect(x, bY, w, h, 2, 2, "F");
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.roundedRect(x, bY, w, h, 2, 2, "S");

    // color accent top bar
    doc.setFillColor(...color);
    doc.roundedRect(x, bY, w, 2.5, 1, 1, "F");

    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C.muted);
    doc.text(title, x + w / 2, bY + 8, { align: "center" });

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...color);
    doc.text(String(value), x + w / 2, bY + 16, { align: "center" });

    if (subtext) {
      doc.setFontSize(6.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C.muted);
      doc.text(subtext, x + w / 2, bY + 21, { align: "center" });
    }
  };

  // ── KPI SECTION ──────────────────────────────────────────────────────────────
  sectionTitle("Key Performance Indicators");

  const kpis = [
    { title: "Total Bookings", value: metrics.total, color: C.accent },
    { title: "Total Guests", value: metrics.guests, color: [139, 92, 246] },
    { title: "Online Orders", value: metrics.online, color: [124, 58, 237] },
    { title: "Offline/Walk-in", value: metrics.offline, color: [14, 165, 233] },
    { title: "Checked Out", value: metrics.checkedOut, color: [82, 196, 26] },
    { title: "Reserved", value: metrics.reserved, color: [22, 119, 255] },
    { title: "Occupied", value: metrics.occupied, color: [250, 173, 20] },
    { title: "Cancelled", value: metrics.cancelled, color: [255, 77, 79] },
  ];

  const kpiW = (contentW - 8 * 3) / 8;
  kpis.forEach((k, i) => {
    statBox(margin + i * (kpiW + 3), y, kpiW, 25, k.title, k.value, k.color);
  });
  y += 30;

  checkPage(22);

  // background strip
  doc.setFillColor(236, 253, 245); // light green
  doc.roundedRect(margin, y, contentW, 20, 3, 3, "F");

  // border
  doc.setDrawColor(16, 185, 129);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, y, contentW, 20, 3, 3, "S");

  // Title
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 116, 139);
  doc.text("TOTAL REVENUE", margin + 6, y + 8);

  // Value (center aligned, large)
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(5, 150, 105);
  doc.text(
    `Rs.${Number(metrics.revenue).toLocaleString("en-IN")}`,
    margin + contentW / 2,
    y + 15,
    { align: "center" }
  );

  y += 26;



  // ── STATUS + PAYMENT SPLIT ───────────────────────────────────────────────────
  sectionTitle("Booking Status & Payment Distribution");

  // Status table (left)
  const statusRows = statusPieData.map(s => {
    const pct = metrics.total > 0 ? ((s.value / metrics.total) * 100).toFixed(1) : "0";
    return [s.name, String(s.value), `${pct}%`];
  });

  doc.autoTable({
    startY: y,
    margin: { left: margin },
    tableWidth: contentW * 0.45,
    head: [["Status", "Count", "Share"]],
    body: statusRows,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: C.headerBg, textColor: C.white, fontStyle: "bold" },
    alternateRowStyles: { fillColor: C.bg },
    columnStyles: { 0: { fontStyle: "bold" }, 2: { halign: "center" } },
    theme: "grid",
  });
  const statusTableEndY = doc.lastAutoTable.finalY;

  // Payment table (right)
  doc.autoTable({
    startY: y,
    margin: { left: margin + contentW * 0.55 },
    tableWidth: contentW * 0.45,
    head: [["Payment Mode", "Count", "Share"]],
    body: paymentModeData.map(p => {
      const pct = metrics.total > 0 ? ((p.value / metrics.total) * 100).toFixed(1) : "0";
      return [p.name, String(p.value), `${pct}%`];
    }),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: C.headerBg, textColor: C.white, fontStyle: "bold" },
    alternateRowStyles: { fillColor: C.bg },
    columnStyles: { 0: { fontStyle: "bold" }, 2: { halign: "center" } },
    theme: "grid",
  });

  y = Math.max(statusTableEndY, doc.lastAutoTable.finalY) + 8;

  // ── ONLINE vs WALK-IN ────────────────────────────────────────────────────────
  sectionTitle("Booking Channel");
  doc.autoTable({
    startY: y,
    margin: { left: margin },
    tableWidth: contentW * 0.5,
    head: [["Channel", "Count", "Share"]],
    body: bookLogModeData.map(d => {
      const total = bookLogModeData.reduce((s, x) => s + x.value, 0);
      const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : "0";
      return [d.name, String(d.value), `${pct}%`];
    }),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241], textColor: C.white, fontStyle: "bold" },
    alternateRowStyles: { fillColor: C.bg },
    theme: "grid",
  });
  y = doc.lastAutoTable.finalY + 8;

  // ── REVENUE BY ROOM ──────────────────────────────────────────────────────────
  checkPage(30);
  sectionTitle("Revenue by Room (Checked Out only)");
  if (revenueByRoom.length > 0) {
    doc.autoTable({
      startY: y,
      margin: { left: margin },
      tableWidth: contentW,
      head: [["Room Name", "Revenue (Rs.)", "Share %"]],
      body: revenueByRoom.map(r => {
        const total = revenueByRoom.reduce((s, x) => s + x.revenue, 0);
        const pct = total > 0 ? ((r.revenue / total) * 100).toFixed(1) : "0";
        return [r.name, Number(r.revenue).toLocaleString("en-IN"), `${pct}%`];
      }),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [16, 185, 129], textColor: C.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: C.bg },
      columnStyles: { 1: { halign: "right", fontStyle: "bold" }, 2: { halign: "center" } },
      theme: "grid",
    });
    y = doc.lastAutoTable.finalY + 8;
  } else {
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text("No revenue data available for selected period.", margin + 4, y + 5);
    y += 12;
  }

  // ── BOOKING TIMELINE ─────────────────────────────────────────────────────────
  checkPage(30);
  sectionTitle("Booking Timeline (Online vs Offline)");
  if (timeline.length > 0) {
    doc.autoTable({
      startY: y,
      margin: { left: margin },
      tableWidth: contentW,
      head: [["Month", "Online", "Offline", "Total"]],
      body: timeline.map(t => [t.month, String(t.Online || 0), String(t.Offline || 0), String((t.Online || 0) + (t.Offline || 0))]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: C.info, textColor: C.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: C.bg },
      columnStyles: { 1: { halign: "center" }, 2: { halign: "center" }, 3: { halign: "center", fontStyle: "bold" } },
      theme: "grid",
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── MOST OCCUPIED ROOMS ───────────────────────────────────────────────────────
  checkPage(30);
  sectionTitle("Top Rooms by Occupancy");
  doc.autoTable({
    startY: y,
    margin: { left: margin },
    tableWidth: contentW,
    head: [["Rank", "Room Name", "Room No.", "Type", "Floor", "Price/Night", "Status", "Log Entries"]],
    body: roomWithLogCount.slice(0, 8).map((r, i) => [
      `#${i + 1}`,
      r.room_name || "-",
      r.room_number || "-",
      r.room_type || "-",
      r.floor || "-",
      `Rs.${Number(r.price_per_day).toLocaleString("en-IN")}`,
      ROOM_STATUS_CFG[r.status]?.label || r.status,
      String(r.log_count),
    ]),
    styles: { fontSize: 7.5, cellPadding: 2.5 },
    headStyles: { fillColor: [245, 158, 11], textColor: C.white, fontStyle: "bold" },
    alternateRowStyles: { fillColor: C.bg },
    columnStyles: {
      0: { halign: "center", fontStyle: "bold" },
      5: { halign: "right" },
      7: { halign: "center", fontStyle: "bold" },
    },
    theme: "grid",
  });
  y = doc.lastAutoTable.finalY + 8;

  // ── ALL ROOMS TABLE ───────────────────────────────────────────────────────────
  checkPage(30);
  sectionTitle("All Rooms Overview");
  doc.autoTable({
    startY: y,
    margin: { left: margin },
    tableWidth: contentW,
    head: [["Room Name", "No.", "Type", "Floor", "Price/Night", "Max Occ.", "Status", "Log Count"]],
    body: roomWithLogCount.map(r => [
      r.room_name || "-",
      r.room_number || "-",
   // REPLACE
String(r.room_type || "-").trim(),
      r.floor || "-",
      `Rs.${Number(r.price_per_day).toLocaleString("en-IN")}`,
      String(r.max_occupancy),
      ROOM_STATUS_CFG[r.status]?.label || r.status || "-",
      String(r.log_count),
    ]),
    styles: { fontSize: 7.5, cellPadding: 2.5 },
    headStyles: { fillColor: C.headerBg, textColor: C.white, fontStyle: "bold" },
    alternateRowStyles: { fillColor: C.bg },
    columnStyles: {
      4: { halign: "right" },
      5: { halign: "center" },
      7: { halign: "center", fontStyle: "bold" },
    },
    theme: "grid",
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 6) {
        const val = data.cell.raw;
        if (val === "Available") data.cell.styles.textColor = [34, 139, 34];
        else if (val === "Occupied") data.cell.styles.textColor = [200, 120, 0];
        else if (val === "Reserved") data.cell.styles.textColor = [22, 119, 255];
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ── BOOKING RECORDS TABLE ─────────────────────────────────────────────────────
  checkPage(30);
  sectionTitle("Booking Records");

  const sortedBookings = [...filteredBookRoom].sort((a, b) => b.id - a.id);

  doc.autoTable({
    startY: y,
    margin: { left: margin },
    tableWidth: contentW,
    head: [["ID", "Guest", "Room", "Check-in", "Check-out", "Nights", "Guests", "Amount", "Payment", "Mode", "Status"]],
    body: sortedBookings.map(b => [
      String(b.id),
      b.guest_name || "-",
      (b.room_name || "-").trim().slice(0, 18),
      b.check_in || "-",
      b.check_out || "-",
      String(b.duration_nights || "-"),
      String(b.guests || 0),
      `Rs.${Number(b.total_amount).toLocaleString("en-IN")}`,
      b.payment_mode || "-",
      b.mode || "walk-in",
      STATUS_CFG[b.guest_status]?.label || b.guest_status || "-",
    ]),
    styles: { fontSize: 6.5, cellPadding: 2 },
    headStyles: { fillColor: [14, 165, 233], textColor: C.white, fontStyle: "bold", fontSize: 7 },
    alternateRowStyles: { fillColor: C.bg },
    columnStyles: {
      0: { halign: "center", cellWidth: 10 },
      5: { halign: "center" },
      6: { halign: "center" },
      7: { halign: "right", fontStyle: "bold" },
    },
    theme: "grid",
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 10) {
        const val = data.cell.raw;
        if (val === "Checked Out") data.cell.styles.textColor = [34, 139, 34];
        else if (val === "Cancelled") { data.cell.styles.textColor = [200, 0, 0]; data.cell.styles.fontStyle = "italic"; }
        else if (val === "Reserved") data.cell.styles.textColor = [22, 119, 255];
        else if (val === "Occupied") data.cell.styles.textColor = [200, 120, 0];
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ── FOOTER ON EVERY PAGE ──────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(...C.white);
    doc.rect(0, pageH - 10, pageW, 10, "F");
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(180, 190, 220);
    // doc.text(`Kadamba Hotel Analytics Report  |  Period: ${fromDt} — ${toDt}  |  Filter: ${statusLabel}`, margin, pageH - 3.5);
    // doc.text(`Sri Chakra Residency Analytics Report  |  Period: ${fromDt} — ${toDt}  |  Filter: ${statusLabel}`, margin, pageH - 3.5);
    doc.text(`Hotel Management Analytics Report  |  Period: ${fromDt} — ${toDt}  |  Filter: ${statusLabel}`, margin, pageH - 3.5);
    doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 3.5, { align: "right" });
  }

  // ── SAVE ──────────────────────────────────────────────────────────────────────
  const fileName = `hotel-report-${dayjs().format("YYYY-MM-DD-HHmm")}.pdf`;
  doc.save(fileName);
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function HotelDashboard() {
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  const [selectedMonth, setSelectedMonth] = useState(dayjs());


  const [statusFilter, setStatusFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  const [book_room, setBookRoom] = useState([]);
  const [book_log, setBookLog] = useState([]);
  const [room, setRoom] = useState([]);

  const filteredBookRoom = useMemo(() => {
    const [from, to] = dateRange || [];
    return book_room.filter((b) => {
      const d = dayjs(b.check_in);
      const inRange = (!from || !d.isBefore(from)) && (!to || !d.isAfter(to));
      const inStatus = statusFilter === "all" || b.guest_status === statusFilter;
      return inRange && inStatus;
    });
  }, [dateRange, statusFilter, book_room]);

  const metrics = useMemo(() => {
    const revenue = filteredBookRoom.filter(b => b.guest_status === "check_out").reduce((s, b) => s + parseFloat(b.total_amount || 0), 0);
    const online = filteredBookRoom.filter(b => b.mode === "online").length;
    const offline = filteredBookRoom.filter(b => b.mode !== "online").length;
    const cancelled = filteredBookRoom.filter(b => b.guest_status === "cancel").length;
    const reserved = filteredBookRoom.filter(b => b.guest_status === "reserved").length;
    const checkedOut = filteredBookRoom.filter(b => b.guest_status === "check_out").length;
    const occupied = filteredBookRoom.filter(b => b.guest_status === "occupied").length;
    const guests = filteredBookRoom.reduce((s, b) => s + (b.guests || 0), 0);
    return { revenue, online, offline, cancelled, reserved, checkedOut, occupied, guests, total: filteredBookRoom.length };
  }, [filteredBookRoom]);

  const bookLogOnlineCount = book_log.filter(l => l.mode === "online").length;
  const bookLogOfflineCount = book_log.filter(l => l.mode === "offline").length;
  const bookLogNullCount = book_log.filter(l => !l.mode).length;
  const bookLogModeData = [
    { name: "Online", value: bookLogOnlineCount },
    { name: "Walk-in", value: bookLogNullCount + bookLogOfflineCount },
  ].filter(d => d.value > 0);

  const getAll_bookRoom = async () => {
    try {
      const req = await GeneralService.get('book_room');
      setBookRoom(req.data?.data || req.data || []);
    } catch (error) { console.error(error); }
  };

  const getAllbook_log = async () => {
    try {
      const req = await GeneralService.get('book_log');
      setBookLog(req.data?.data || req.data || []);
    } catch (error) { console.error(error); }
  };

  const getAllRoom = async () => {
    try {
      const req = await GeneralService.get('room');
      setRoom(req.data?.data || req.data || []);
    } catch (error) { console.error(error); }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        if (isMounted) await Promise.all([getAll_bookRoom(), getAllbook_log(), getAllRoom()]);
      } catch (error) { console.error("Error fetching data:", error); }
    };
    fetchData();
    return () => { isMounted = false; };
  }, []);

  const statusPieData = useMemo(() => [
    { name: "Checked Out", value: metrics.checkedOut },
    { name: "Reserved", value: metrics.reserved },
    { name: "Cancelled", value: metrics.cancelled },
    { name: "Occupied", value: metrics.occupied },
  ].filter(d => d.value > 0), [metrics]);

  const revenueByRoom = useMemo(() => {
    const map = {};
    room.forEach(r => { map[r.id] = { name: r.room_name?.split(" ").slice(0, 3).join(" ") || r.room_name, revenue: 0 }; });
    filteredBookRoom.filter(b => b.guest_status === "check_out")
      .forEach(b => { if (map[b.room_id]) map[b.room_id].revenue += parseFloat(b.total_amount || 0); });
    return Object.values(map).filter(r => r.revenue > 0);
  }, [filteredBookRoom, room]);

  const timeline = useMemo(() => {
    const map = {};
    filteredBookRoom.forEach(b => {
      const m = dayjs(b.check_in).format("MMM YY");
      if (!map[m]) map[m] = { month: m, Online: 0, Offline: 0 };
      if (b.mode === "online") map[m].Online++; else map[m].Offline++;
    });
    return Object.values(map).sort((a, b) => dayjs(a.month, "MMM YY") - dayjs(b.month, "MMM YY"));
  }, [filteredBookRoom]);

  const roomWithLogCount = useMemo(() => {
    const counts = {};
    room.forEach(r => { counts[r.id] = 0; });
    book_log.forEach(l => { if (counts[l.room_id] !== undefined) counts[l.room_id]++; });
    return room.map(r => ({ ...r, log_count: counts[r.id] || 0 })).sort((a, b) => b.log_count - a.log_count);
  }, [room, book_log]);

  const paymentModeData = useMemo(() => {
    const map = {};
    filteredBookRoom.forEach(b => {
      const mode = b.payment_mode || "Other";
      map[mode] = (map[mode] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [filteredBookRoom]);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await exportDashboardPDF({
        dateRange,
        statusFilter,
        metrics,
        filteredBookRoom,
        room,
        revenueByRoom,
        roomWithLogCount,
        paymentModeData,
        bookLogModeData,
        statusPieData,
        timeline,
      });
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const bookRoomCols = [
    { title: "ID", dataIndex: "id", key: "id", width: 55 },
    { title: "Guest", dataIndex: "guest_name", key: "guest_name", render: t => <Text strong style={{ textTransform: "capitalize" }}>{t || "-"}</Text> },
    { title: "Room", dataIndex: "room_name", key: "room_name", render: t => <Text type="secondary" style={{ fontSize: 12 }}>{t || "-"}</Text>, ellipsis: true },
    { title: "Check-in", dataIndex: "check_in", key: "check_in", render: v => v || "-" },
    { title: "Check-out", dataIndex: "check_out", key: "check_out", render: v => v || "-" },
    { title: "Guests", dataIndex: "guests", key: "guests", align: "center", render: v => <Tag>{v || 0}</Tag> },
    { title: "Nights", dataIndex: "duration_nights", key: "duration_nights", align: "center", render: v => v || "-" },
    { title: "Amount", dataIndex: "total_amount", key: "total_amount", render: v => <Text strong style={{ color: "#52c41a" }}>{fmt(v)}</Text>, sorter: (a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount) },
    { title: "Payment", dataIndex: "payment_mode", key: "payment_mode", render: v => <Tag color="blue">{v || "-"}</Tag> },
    { title: "Mode", dataIndex: "mode", key: "mode", render: v => v === "online" ? <Tag color="purple" icon={<GlobalOutlined />}>Online</Tag> : v === "offline" ? <Tag color="cyan" icon={<ShopOutlined />}>Offline</Tag> : <Tag>Walk-in</Tag> },
    { title: "Status", dataIndex: "guest_status", key: "guest_status", render: v => <Tag color={STATUS_CFG[v]?.color}>{STATUS_CFG[v]?.label || v}</Tag>, filters: Object.entries(STATUS_CFG).map(([k, v]) => ({ text: v.label, value: k })), onFilter: (val, rec) => rec.guest_status === val },
  ];

  const bookLogCols = [
    { title: "Log ID", dataIndex: "id", key: "id", width: 70 },
    { title: "Room", dataIndex: "room_id", key: "room_id", render: v => { const r = room.find(x => x.id === v); return <Tag color="geekblue">#{v} · {r?.room_number || "?"}</Tag>; } },
    { title: "Check-in", dataIndex: "check_in", key: "check_in", render: v => v || "-" },
    { title: "Check-out", dataIndex: "check_out", key: "check_out", render: v => v || "-" },
    { title: "In Time", dataIndex: "check_in_time", key: "check_in_time", render: v => v || "-" },
    { title: "Actual Checkout", dataIndex: "act_check_out_date", key: "act_check_out_date", render: v => v || <Text type="secondary">—</Text> },
    { title: "Mode", dataIndex: "mode", key: "mode", render: v => v ? <Tag color={v === "online" ? "purple" : "cyan"}>{v}</Tag> : <Text type="secondary">—</Text> },
    { title: "Note", dataIndex: "note", key: "note", render: v => v || <Text type="secondary">—</Text>, ellipsis: true },
    { title: "Status", dataIndex: "status", key: "status", render: v => <Tag color={STATUS_CFG[v]?.color}>{STATUS_CFG[v]?.label || v}</Tag> },
  ];

  const roomCols = [
    { title: "Room Name", dataIndex: "room_name", key: "room_name", render: t => <Text strong>{t}</Text>, ellipsis: true },
    { title: "No.", dataIndex: "room_number", key: "room_number" },
    { title: "Type", dataIndex: "room_type", key: "room_type", render: t => <Tag color="geekblue">{t}</Tag> },
    { title: "Floor", dataIndex: "floor", key: "floor", align: "center" },
    { title: "Status", dataIndex: "status", key: "status", render: v => <Badge color={ROOM_STATUS_CFG[v]?.color} text={<Text style={{ color: ROOM_STATUS_CFG[v]?.color, fontWeight: 500 }}>{ROOM_STATUS_CFG[v]?.label}</Text>} /> },
    { title: "Price/Night", dataIndex: "price_per_day", key: "price_per_day", render: v => <Text strong>{fmt(v)}</Text> },
    { title: "Max Occ.", dataIndex: "max_occupancy", key: "max_occupancy", align: "center" },
    {
      title: "book_log Count", dataIndex: "log_count", key: "log_count", align: "center", defaultSortOrder: "descend",
      sorter: (a, b) => a.log_count - b.log_count,
      render: (v, record) => <Space><Tag color={v >= 8 ? "red" : v >= 5 ? "orange" : "green"} style={{ fontWeight: 700, minWidth: 28, textAlign: "center" }}>{v}</Tag><Progress percent={record.log_count && roomWithLogCount[0]?.log_count ? Math.min(100, Math.round(v / roomWithLogCount[0].log_count * 100)) : 0} size="small" showInfo={false} style={{ width: 70 }} strokeColor={v >= 8 ? "#ff4d4f" : v >= 5 ? "#faad14" : "#52c41a"} /></Space>,
    },
  ];

  const StatCard = ({ title, value, icon, color, sub }) => (
    <Card size="small" style={{ borderRadius: 12, height: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>{title}</Text>
          <div style={{ marginTop: 4 }}>
            <Text style={{ fontSize: 21, fontWeight: 700, color: color || "inherit" }}>{value}</Text>
          </div>
        </div>
        <div style={{ background: `${color}18`, borderRadius: 8, padding: 8, color }}>{icon}</div>
      </div>
    </Card>
  );

  const totalsSata = [
    { title: "Total Bookings", value: metrics.total, color: "#6366f1", icon: <CalendarOutlined /> },
    { title: "Total Guests", value: metrics.guests, color: "#8b5cf6", icon: <TeamOutlined /> },
  ];

  const stats = [
    { title: "Revenue", value: fmt(metrics.revenue), color: "#52c41a", icon: <DollarOutlined /> },
    { title: "Online Orders", value: metrics.online, color: "#7c3aed", icon: <GlobalOutlined /> },
    { title: "Offline/Walk-in", value: metrics.offline, color: "#0ea5e9", icon: <ShopOutlined /> },
    { title: "Checked Out", value: metrics.checkedOut, color: "#52c41a", icon: <CheckCircleOutlined /> },
    { title: "Reserved", value: metrics.reserved, color: "#1677ff", icon: <ClockCircleOutlined /> },
    { title: "Occupied", value: metrics.occupied, color: "#faad14", icon: <RiseOutlined /> },
    { title: "Cancelled", value: metrics.cancelled, color: "#ff4d4f", icon: <CloseCircleOutlined /> },
  ];
  if (!canViewModule(MODULE_KEY)) {
    return <Empty description="No permission to view this page" />;
  }

  return (
    <Card>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Analytics Dashboard</Title>
        </div>
        <Space wrap>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
            options={[{ value: "all", label: "All Statuses" }, ...Object.entries(STATUS_CFG).map(([k, v]) => ({ value: k, label: v.label }))]}
          />

          <DatePicker
            picker="month"
            allowClear={true}
            value={selectedMonth}
            format="MMMM YYYY"
            onChange={(value) => {
              setSelectedMonth(value);

              if (value) {
                setDateRange([
                  value.startOf("month").startOf("day"),
                  value.endOf("month").endOf("day"),
                ]);
              }
            }}
          />
          {/* ── DATE + TIME RANGE PICKER ── */}
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            showTime={{ format: "hh:mm A", use12Hours: true }}
            format="DD MMM YYYY hh:mm A"
            style={{ borderRadius: 8 }}
            placeholder={["From date & time", "To date & time"]}
          />

          {/* <Button
            onClick={() => {
              setDateRange([dayjs("2026-03-01").startOf("day"), dayjs("2026-05-31").endOf("day")]);
              setStatusFilter("all");
            }}
          >
            Reset
          </Button> */}

          <Button
            onClick={() => {

              const defaultMonth = dayjs().startOf("month");
              setDateRange();
              setSelectedMonth(dayjs());
              setStatusFilter("all");

              setDateRange([
                defaultMonth.startOf("day"),
                defaultMonth.endOf("day"),
              ]);
            }}
          >
            Reset
          </Button>

          {/* ── EXPORT PDF BUTTON ── */}
          <AntTooltip title="Export full dashboard as PDF (A4)">
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              loading={exporting}
              onClick={handleExportPDF}
              style={{ background: "#e53e3e", borderColor: "#e53e3e" }}
            >
              {exporting ? "Generating..." : "Export PDF"}
            </Button>
          </AntTooltip>
        </Space>
      </div>

      <Divider />

      <Row gutter={[10, 10]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={6}>
          <Row gutter={[10, 10]}>
            {totalsSata.map((s, i) => (
              <Col span={24} key={i}><StatCard {...s} /></Col>
            ))}
          </Row>
        </Col>
        <Col xs={24} md={18}>
          <Row gutter={[10, 10]}>
            {stats.slice(0, 4).map((s, i) => (
              <Col xs={12} sm={12} md={6} key={i}><StatCard {...s} /></Col>
            ))}
          </Row>
          <Row gutter={[10, 10]} style={{ marginTop: 10 }}>
            {stats.slice(4, 7).map((s, i) => (
              <Col xs={12} sm={12} md={8} key={i}><StatCard {...s} /></Col>
            ))}
          </Row>
        </Col>
      </Row>

      {/* Charts Row 1 */}
      <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
        <Col xs={24} md={8}>
          <Card title={<><GlobalOutlined style={{ color: "#6366f1", marginRight: 6 }} />Online Orders</>} bordered={false} style={{ borderRadius: 12 }}>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={bookLogModeData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                  {bookLogModeData.map((_, i) => <Cell key={i} fill={["#6366f1", "#0ea5e9", "#94a3b8"][i]} />)}
                </Pie>
                <Tooltip /><Legend iconType="circle" iconSize={9} />
              </PieChart>
            </ResponsiveContainer>
            <Row gutter={6} style={{ marginTop: 6 }}>
              {bookLogModeData.map((d, i) => (
                <Col key={i} span={Math.floor(24 / bookLogModeData.length)}>
                  <div style={{ background: ["#f0f0ff", "#e0f2fe", "#f1f5f9"][i], borderRadius: 8, padding: "6px 0", textAlign: "center" }}>
                    <div style={{ color: ["#6366f1", "#0ea5e9", "#64748b"][i], fontWeight: 700, fontSize: 20 }}>{d.value}</div>
                    <div style={{ fontSize: 10, color: "#888" }}>{d.name}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title={<><CheckCircleOutlined style={{ color: "#52c41a", marginRight: 6 }} />Status Split</>} bordered={false} style={{ borderRadius: 12 }}>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={statusPieData} cx="50%" cy="50%" outerRadius={80} paddingAngle={2} dataKey="value" label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {statusPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie>
                <Tooltip /><Legend iconType="square" iconSize={9} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title={<><DollarOutlined style={{ color: "#faad14", marginRight: 6 }} />Payment Mode</>} bordered={false} style={{ borderRadius: 12 }}>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={paymentModeData} cx="50%" cy="50%" outerRadius={80} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}(${value})`}>
                  {paymentModeData.map((_, i) => <Cell key={i} fill={["#6366f1", "#0ea5e9", "#52c41a", "#faad14", "#f97316"][i % 5]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2 */}
      <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
        <Col xs={24} md={12}>
          <Card title={<><DollarOutlined style={{ color: "#52c41a", marginRight: 6 }} />Revenue by Room</>} bordered={false} style={{ borderRadius: 12 }}>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={revenueByRoom} margin={{ top: 14, right: 10, left: 10, bottom: 45 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" interval={0} />
                <YAxis tickFormatter={v => `Rs.${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" name="Revenue" radius={[6, 6, 0, 0]}>
                  {revenueByRoom.map((_, i) => <Cell key={i} fill={["#6366f1", "#0ea5e9", "#52c41a", "#faad14"][i % 4]} />)}
                  <LabelList dataKey="revenue" position="top" formatter={v => v > 0 ? `Rs.${(v / 1000).toFixed(1)}k` : ""} style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title={<><CalendarOutlined style={{ color: "#1677ff", marginRight: 6 }} />Booking Timeline — Online vs Offline</>} bordered={false} style={{ borderRadius: 12 }}>
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={timeline} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend iconType="circle" iconSize={9} />
                <Line type="monotone" dataKey="Online" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 5 }} activeDot={{ r: 7 }} />
                <Line type="monotone" dataKey="Offline" stroke="#0ea5e9" strokeWidth={2.5} strokeDasharray="5 3" dot={{ r: 5 }} activeDot={{ r: 7 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Room Ranking */}
      <Card title={<><RiseOutlined style={{ color: "#faad14", marginRight: 6 }} />Highest Occupied / Requested</>} bordered={false} style={{ borderRadius: 12, marginBottom: 14 }}>
        <Row gutter={[12, 12]}>
          {roomWithLogCount.slice(0, 4).map((r, i) => (
            <Col key={r.id} xs={24} sm={12} md={6}>
              <Card size="small" style={{ borderRadius: 10, border: i === 0 ? "2px solid #faad14" : "1px solid #f0f0f0", height: "100%" }}>
                <Space style={{ marginBottom: 6 }}>
                  <Tag color={i === 0 ? "gold" : i === 1 ? "silver" : "default"} style={{ fontWeight: 700 }}>#{i + 1}</Tag>
                  <Text strong style={{ fontSize: 12 }}>Room {r.room_number}</Text>
                </Space>
                <div style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>{r.room_name}</div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Entries</Text>
                  <Text strong style={{ color: i === 0 ? "#faad14" : "#333" }}>{r.log_count}</Text>
                </div>
                <Progress percent={roomWithLogCount[0]?.log_count ? Math.round(r.log_count / roomWithLogCount[0].log_count * 100) : 0} showInfo={false} strokeColor={["#faad14", "#6366f1", "#52c41a", "#94a3b8"][i]} size={["100%", 7]} />
                <div style={{ marginTop: 8 }}>
                  <Badge color={ROOM_STATUS_CFG[r.status]?.color} text={<Text style={{ fontSize: 11, color: ROOM_STATUS_CFG[r.status]?.color }}>{ROOM_STATUS_CFG[r.status]?.label}</Text>} />
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Room table */}
      <Card title={<><HomeOutlined style={{ color: "#6366f1", marginRight: 6 }} />All Rooms</>} bordered={false} style={{ borderRadius: 12, marginBottom: 14 }}>
        <Table dataSource={roomWithLogCount} columns={roomCols} rowKey="id" pagination={false} size="middle" scroll={{ x: 700 }} />
      </Card>

      {/* Booking records table */}
      <Card
        title={<><CalendarOutlined style={{ color: "#0ea5e9", marginRight: 6 }} />All Booking Records</>}
        bordered={false} style={{ borderRadius: 12, marginBottom: 14 }}
        extra={<Text type="secondary">{filteredBookRoom.length} records</Text>}
      >
        <Table
          dataSource={[...filteredBookRoom].sort((a, b) => b.id - a.id)}
          columns={bookRoomCols} rowKey="id"
          pagination={{ pageSize: 8, showSizeChanger: true, showTotal: t => `Total ${t}` }}
          size="middle" scroll={{ x: 1100 }}
          rowClassName={rec => rec.guest_status === "cancel" ? "cancelled-row" : ""}
        />
      </Card>

      {/* book_log table */}
      <Card
        title={<><ClockCircleOutlined style={{ color: "#faad14", marginRight: 6 }} />Raw Log Entries</>}
        bordered={false} style={{ borderRadius: 12 }}
        extra={<Text type="secondary">{book_log.length} total logs</Text>}
      >
        <Table dataSource={book_log} columns={bookLogCols} rowKey="id"
          pagination={{ pageSize: 8, showSizeChanger: true }} size="middle" scroll={{ x: 900 }} />
      </Card>

      <style>{`
        .cancelled-row td { opacity: 0.5; background: #fff5f5 !important; }
        .ant-table-thead > tr > th { background: #fafafa !important; font-weight: 600; font-size: 12px; }
        code { background: #f0f0f0; padding: 1px 5px; border-radius: 4px; font-size: 12px; }
      `}</style>
    </Card>
  );
}